import { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  HiLockClosed, HiShieldCheck, HiArrowLeft, HiArrowRight, HiTruck,
  HiMail, HiPhone, HiUser, HiHome, HiCheck, HiChevronDown, HiChevronUp, HiSparkles
} from 'react-icons/hi';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const IMAGE_BASE =  process.env.REACT_APP_API_URL;
const VAT_RATE = 0.075;

const NIGERIAN_STATES = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa',
  'Benue', 'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo',
  'Ekiti', 'Enugu', 'FCT', 'Gombe', 'Imo', 'Jigawa',
  'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara',
  'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun',
  'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

// ---------- Sub-components (defined outside to avoid re-creation) ----------

function StepDot({ active, done, label, num }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-semibold border-2 transition-all duration-300 ${
        active ? 'border-gray-900 bg-gray-900 text-white shadow-md' : 
        done ? 'border-emerald-500 bg-emerald-500 text-white' : 
        'border-gray-200 bg-white text-gray-400'
      }`}>
        {done ? <HiCheck size={20} /> : num}
      </div>
      <span className={`text-[11px] font-semibold uppercase tracking-widest ${active ? 'text-gray-900' : 'text-gray-400'}`}>
        {label}
      </span>
    </div>
  );
}

function Field({ children, error }) {
  return (
    <div>
      {children}
      {error && (
        <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
          <span className="inline-block w-1 h-1 rounded-full bg-red-500"></span>
          {error}
        </p>
      )}
    </div>
  );
}

function OrderSummary({
  cartItems, formatCurrency, couponCode, setCouponCode,
  discount, appliedCode, applyCoupon, removeCoupon,
  subtotal, vat, delivery, total, imageBase,
}) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-base font-bold text-gray-900">Order Summary</h2>
        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
          {cartItems.length} item{cartItems.length !== 1 && 's'}
        </span>
      </div>

      <div className="space-y-4 mb-6 max-h-[320px] overflow-y-auto pr-1 -mr-1 scrollbar-thin scrollbar-thumb-gray-200">
        {cartItems.map((item) => (
          <div key={item.key} className="flex gap-3 group">
            <div className="relative w-16 h-16 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0 shadow-sm group-hover:shadow-md transition-shadow">
              <img
                src={item.image ? (item.image.startsWith('/uploads/') ? `${imageBase}/images/${item.image.replace('/uploads/', '')}` : item.image) : 'https://via.placeholder.com/150'}
                alt={item.name}
                className="w-full h-full object-cover"
              />
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-900 text-white text-[10px] font-bold grid place-items-center shadow">
                {item.quantity}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {item.size && `${item.size} • `}{item.color}
              </p>
            </div>
            <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
              {formatCurrency(item.price * item.quantity)}
            </p>
          </div>
        ))}
      </div>

      <div className="mb-5 pt-5 border-t border-gray-100">
        {discount === 0 ? (
          <div className="flex gap-2">
            <input
              type="text" value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="Promo code"
              className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:border-gray-900 focus:ring-2 focus:ring-gray-900/5 outline-none transition-all"
            />
            <button
              onClick={applyCoupon}
              className="px-5 bg-gray-900 text-white rounded-xl font-semibold text-sm hover:bg-black transition-colors active:scale-95"
            >
              Apply
            </button>
          </div>
        ) : (
          <div className="flex justify-between items-center bg-emerald-50 text-emerald-700 px-4 py-3 rounded-xl">
            <span className="font-medium text-sm flex items-center gap-1.5"><HiCheck className="text-emerald-600" /> {appliedCode}</span>
            <button onClick={removeCoupon} className="text-red-600 hover:underline text-xs font-semibold">Remove</button>
          </div>
        )}
      </div>

      <div className="space-y-2.5 text-sm border-t border-gray-100 pt-5">
        <Row label="Subtotal" value={formatCurrency(subtotal)} />
        <Row label="VAT (7.5%)" value={formatCurrency(vat)} />
        <Row label="Shipping" value={delivery === 0 ? 'Free' : formatCurrency(delivery)} valueClass={delivery === 0 ? 'text-emerald-600 font-medium' : ''} />
        {discount > 0 && <Row label="Discount" value={`-${formatCurrency(discount)}`} className="text-emerald-600" valueClass="text-emerald-600 font-medium" />}
      </div>

      <div className="border-t border-gray-200 mt-5 pt-5 flex justify-between items-baseline">
        <span className="text-sm font-medium text-gray-600">Total</span>
        <span className="text-xl font-bold text-gray-900">{formatCurrency(total)}</span>
      </div>

      <div className="flex justify-center items-center gap-1.5 text-[11px] text-gray-400 mt-5">
        <HiShieldCheck className="text-emerald-500" /> Secure checkout
      </div>
    </div>
  );
}

function Row({ label, value, className = '', valueClass = '' }) {
  return (
    <div className={`flex justify-between ${className}`}>
      <span className="text-gray-600">{label}</span>
      <span className={`text-gray-900 ${valueClass}`}>{value}</span>
    </div>
  );
}

// ---------- Main Checkout Component ----------

export default function Checkout() {
  const { cartItems, totalPrice, clearCart } = useCart();
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: user?.email || '', phone: '',
    country: 'Nigeria', address: '', apartment: '', city: '', state: 'Lagos',
    postalCode: '', deliveryNotes: '',
  });
  const [errors, setErrors] = useState({});
  const [shippingLocations, setShippingLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [shippingCost, setShippingCost] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [appliedCode, setAppliedCode] = useState('');

  const [flutterwaveKey] = useState(
    process.env.REACT_APP_FLUTTERWAVE_PUBLIC_KEY 
  );

  useEffect(() => {
    axios.get(`${API}/api/shipping/locations`)
      .then(res => setShippingLocations(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (cartItems.length === 0 && !orderPlaced) navigate('/cart');
  }, [cartItems, navigate, orderPlaced]);

  const handleLocationChange = async (e) => {
    const loc = e.target.value;
    setSelectedLocation(loc);
    if (!loc) { setShippingCost(0); return; }
    try {
      const res = await axios.get(`${API}/api/shipping/cost?location=${loc}`);
      setShippingCost(res.data);
    } catch { setShippingCost(0); }
  };

  const subtotal = totalPrice;
  const vat = Math.round(subtotal * VAT_RATE);
  const delivery = shippingCost;
  const total = subtotal + vat + delivery - discount;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateShipping = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'Required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Required';
    if (!formData.email.trim()) newErrors.email = 'Required';
    if (!formData.phone.trim()) newErrors.phone = 'Required';
    if (!formData.address.trim()) newErrors.address = 'Required';
    if (!formData.city.trim()) newErrors.city = 'Required';
    if (formData.country === 'Nigeria' && !formData.state) newErrors.state = 'Select a state';
    if (!selectedLocation) newErrors.shippingLocation = 'Select a delivery location';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinueToPayment = (e) => {
    e.preventDefault();
    if (validateShipping()) {
      setStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code || discount > 0) return;
    try {
      const res = await axios.post(`${API}/api/coupons/validate`, { code });
      const discountAmount = Math.round((subtotal * res.data.discount) / 100);
      setDiscount(discountAmount);
      setAppliedCode(code);
      setCouponCode('');
    } catch (err) {
      alert(err.response?.data || 'Invalid coupon code');
    }
  };

  const removeCoupon = () => { setDiscount(0); setAppliedCode(''); };

  const handlePayment = async () => {
    if (!user) {
      alert('Please log in first');
      navigate('/login');
      return;
    }
    setLoading(true);

    const orderPayload = {
      items: cartItems.map(item => ({
        productId: item.productId, productName: item.name, price: item.price,
        quantity: item.quantity, size: item.size, color: item.color, image: item.image,
      })),
      total,
      status: 'PENDING',
      shippingAddress: formData,
      shippingCost,
      couponCode: appliedCode,
      discountPercentage: appliedCode ? Math.round((discount / subtotal) * 100) : 0,
      phone: formData.phone,
      paymentMethod: 'flutterwave',
    };

    try {
      const resOrder = await axios.post(`${API}/api/orders`, orderPayload);
      const orderId = resOrder.data.id;
      const orderRef = `ORDER_${orderId}_${Date.now()}`;

      const flwConfig = {
        public_key: flutterwaveKey,
        tx_ref: orderRef,
        amount: total,
        currency: 'NGN',
        payment_options: 'card,ussd,banktransfer,account',
        customer: {
          email: user.email,
          name: `${formData.firstName} ${formData.lastName}`,
          phonenumber: formData.phone,
        },
        customizations: {
          title: 'WAVEYYENT',
          description: `Payment for order #${orderId}`,
          logo: 'https://yourdomain.com/logo.png', // optional
        },
        meta: {
          order_id: orderId,
        },
        callback: (response) => {
          verifyFlutterwavePayment(response.transaction_id, orderId);
        },
        onclose: () => {
          setLoading(false);
        },
      };

      if (window.FlutterwaveCheckout) {
        window.FlutterwaveCheckout(flwConfig);
      } else {
        alert('Flutterwave SDK not loaded');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to create order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyFlutterwavePayment = async (transactionId, orderId) => {
    try {
      await axios.post(`${API}/api/flutterwave/verify/${orderId}`,
        { transaction_id: transactionId },
        { headers: { Authorization: '' } }   // public endpoint
      );
      setOrderPlaced(true);
      clearCart();
      navigate(`/order-confirmation/${orderId}`);
    } catch (err) {
      alert('Payment verification failed. Please contact support.');
    }
  };

  if (cartItems.length === 0) return null;

  const inputBase = "w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-[15px] placeholder:text-gray-400 focus:bg-white focus:border-gray-900 focus:ring-2 focus:ring-gray-900/5 transition-all outline-none";
  const inputPlain = "w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-[15px] focus:bg-white focus:border-gray-900 focus:ring-2 focus:ring-gray-900/5 transition-all outline-none";
  const labelCls = "block text-[13px] font-semibold text-gray-700 mb-1.5";

  return (
    <div className="min-h-screen bg-gray-50 pb-32 lg:pb-0">
      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/cart" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors group">
            <HiArrowLeft className="mr-1.5 transition-transform group-hover:-translate-x-0.5" />
            <span className="hidden sm:inline">Back to Cart</span>
            <span className="sm:hidden">Back</span>
          </Link>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
            <HiLockClosed className="text-emerald-600" />
            Secure Checkout
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        {/* Steps */}
        <div className="mb-8 lg:mb-10">
          <div className="flex items-center justify-center max-w-md mx-auto">
            <StepDot active={step === 1} done={step > 1} label="Shipping" num={1} />
            <div className={`flex-1 h-0.5 mx-2 rounded-full transition-colors duration-500 ${step >= 2 ? 'bg-gray-900' : 'bg-gray-200'}`} />
            <StepDot active={step === 2} done={false} label="Payment" num={2} />
          </div>
        </div>

        {/* Mobile order summary toggle */}
        <div className="lg:hidden mb-6">
          <button
            onClick={() => setSummaryOpen(o => !o)}
            className="w-full flex items-center justify-between bg-white rounded-2xl border border-gray-100 px-5 py-4 shadow-sm"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
              {summaryOpen ? <HiChevronUp /> : <HiChevronDown />}
              {summaryOpen ? 'Hide' : 'Show'} order summary ({cartItems.length})
            </span>
            <span className="font-bold">{formatCurrency(total)}</span>
          </button>
          {summaryOpen && (
            <div className="mt-3">
              <OrderSummary
                cartItems={cartItems} formatCurrency={formatCurrency}
                couponCode={couponCode} setCouponCode={setCouponCode}
                discount={discount} appliedCode={appliedCode}
                applyCoupon={applyCoupon} removeCoupon={removeCoupon}
                subtotal={subtotal} vat={vat} delivery={delivery} total={total}
                imageBase={IMAGE_BASE}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Main content */}
          <div className="lg:col-span-7 xl:col-span-8">
            {step === 1 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-8 lg:p-10">
                <div className="mb-7">
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">Shipping details</h1>
                  <p className="text-sm text-gray-500 mt-1">Where should we deliver your order?</p>
                </div>

                <form onSubmit={handleContinueToPayment} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <Field error={errors.firstName}>
                      <label className={labelCls}>First name</label>
                      <div className="relative">
                        <HiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className={inputBase} placeholder="John" />
                      </div>
                    </Field>
                    <Field error={errors.lastName}>
                      <label className={labelCls}>Last name</label>
                      <div className="relative">
                        <HiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className={inputBase} placeholder="Doe" />
                      </div>
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <Field error={errors.email}>
                      <label className={labelCls}>Email address</label>
                      <div className="relative">
                        <HiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="email" name="email" value={formData.email} onChange={handleInputChange} className={inputBase} placeholder="you@example.com" />
                      </div>
                    </Field>
                    <Field error={errors.phone}>
                      <label className={labelCls}>Phone number</label>
                      <div className="relative">
                        <HiPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className={inputBase} placeholder="+234 801 234 5678" />
                      </div>
                    </Field>
                  </div>

                  <Field error={errors.address}>
                    <label className={labelCls}>Street address</label>
                    <div className="relative">
                      <HiHome className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="text" name="address" value={formData.address} onChange={handleInputChange} className={inputBase} placeholder="123 Example Street" />
                    </div>
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <Field error={errors.country}>
                      <label className={labelCls}>Country</label>
                      <input type="text" name="country" value={formData.country} onChange={handleInputChange} className={inputPlain} placeholder="Nigeria" />
                    </Field>
                    <Field error={errors.city}>
                      <label className={labelCls}>City</label>
                      <input type="text" name="city" value={formData.city} onChange={handleInputChange} className={inputPlain} placeholder="Lagos" />
                    </Field>
                  </div>

                  <Field error={errors.state}>
                    <label className={labelCls}>State / Region</label>
                    {formData.country === 'Nigeria' ? (
                      <select name="state" value={formData.state} onChange={handleInputChange} className={inputPlain}>
                        <option value="">Select state...</option>
                        {NIGERIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <input type="text" name="state" value={formData.state} onChange={handleInputChange} className={inputPlain} placeholder="Region / State" />
                    )}
                  </Field>

                  <Field error={errors.shippingLocation}>
                    <label className={`${labelCls} flex items-center gap-1.5`}>
                      <HiTruck className="text-gray-500" /> Delivery location
                    </label>
                    <select value={selectedLocation} onChange={handleLocationChange} className={inputPlain}>
                      <option value="">Select delivery location...</option>
                      {shippingLocations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                    </select>
                    {shippingCost > 0 && (
                      <p className="text-emerald-600 text-xs mt-2 flex items-center gap-1">
                        <HiCheck /> Shipping: {formatCurrency(shippingCost)}
                      </p>
                    )}
                  </Field>

                  <button
                    type="submit"
                    disabled={!selectedLocation}
                    className="group relative w-full overflow-hidden bg-gray-900 text-white py-4 rounded-2xl font-semibold text-base tracking-wide transition-all duration-300 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:bg-black hover:shadow-lg hover:shadow-gray-900/20 active:scale-[0.99]"
                  >
                    <span>Continue to Payment</span>
                    <HiArrowRight className="transition-transform duration-300 group-hover:translate-x-1" size={20} />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 pointer-events-none" />
                  </button>
                </form>
              </div>
            )}

            {step === 2 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-8 lg:p-10">
                <div className="mb-7">
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">Payment</h1>
                  <p className="text-sm text-gray-500 mt-1">Complete your purchase securely with Flutterwave</p>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center text-3xl">
                      💳
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">Flutterwave</h3>
                      <p className="text-sm text-gray-500">Cards, Bank Transfer, USSD & more</p>
                    </div>
                    <div className="ml-auto">
                      <HiShieldCheck className="text-emerald-500 text-2xl" />
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setStep(1)}
                  className="mt-6 text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 font-medium"
                >
                  <HiArrowLeft size={14} /> Back to shipping
                </button>
              </div>
            )}

            {/* Trust badges */}
            <div className="hidden sm:grid grid-cols-2 gap-3 mt-6">
              {[
                { icon: <HiShieldCheck />, title: 'SSL Encrypted', sub: '256-bit security' },
                { icon: <HiTruck />, title: 'Fast Delivery', sub: 'Tracked shipping' },
              ].map((t, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-3 shadow-sm">
                  <div className="w-9 h-9 rounded-lg bg-gray-100 grid place-items-center text-gray-700">{t.icon}</div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-900 truncate">{t.title}</p>
                    <p className="text-[11px] text-gray-500 truncate">{t.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop order summary */}
          <div className="hidden lg:block lg:col-span-5 xl:col-span-4">
            <div className="sticky top-24">
              <OrderSummary
                cartItems={cartItems} formatCurrency={formatCurrency}
                couponCode={couponCode} setCouponCode={setCouponCode}
                discount={discount} appliedCode={appliedCode}
                applyCoupon={applyCoupon} removeCoupon={removeCoupon}
                subtotal={subtotal} vat={vat} delivery={delivery} total={total}
                imageBase={IMAGE_BASE}
              />
              {step === 2 && (
                <button
                  onClick={handlePayment} disabled={loading}
                  className="w-full mt-4 bg-gray-900 text-white py-4 rounded-2xl font-bold text-base hover:bg-black transition-all active:scale-[0.99] flex items-center justify-center gap-2 disabled:opacity-60 shadow-lg shadow-gray-900/20"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    <><HiLockClosed /> Pay {formatCurrency(total)}</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile fixed payment button */}
      {step === 2 && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">Total</span>
            <span className="font-bold text-lg">{formatCurrency(total)}</span>
          </div>
          <button
            onClick={handlePayment} disabled={loading}
            className="w-full bg-gray-900 text-white py-3.5 rounded-xl font-bold text-base flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.99] transition shadow-lg shadow-gray-900/20"
          >
            {loading ? 'Processing...' : <><HiLockClosed /> Pay securely</>}
          </button>
        </div>
      )}
    </div>
  );
} 